import React, { useState } from "react";
import { Menu, X, BookOpen, Download, HardHat, FileText, BarChart, Zap } from "lucide-react";

// --- Enhanced Sidebar Items ---
const sidebarItems = [
    { id: "ut-level-2", label: "UT Level 2 Course", icon: <BookOpen size={20} /> },
    { id: "paut-level-2", label: "PAUT Level 2 Course", icon: <BookOpen size={20} /> },
    { id: "simulations", label: "Interactive Simulations", icon: <Zap size={20} /> },
    { id: "diagrams", label: "Technical Schematics", icon: <BarChart size={20} /> },
    { id: "tools", label: "NDT Calculators & Tools", icon: <HardHat size={20} /> },
    { id: "articles", label: "Expert NDT Articles", icon: <FileText size={20} /> },
    { id: "downloads", label: "Premium Downloads", icon: <Download size={20} /> },
];

// Define constants for header heights
const MOBILE_HEADER_HEIGHT_CLASS = "h-14"; // 56px
const DESKTOP_NAVBAR_HEIGHT_CLASS = "h-16"; // Assuming a top navbar of 64px

// --- Content Data (omitted for brevity, assume the previous structure with the fixes) ---
const contentData = {
"ut-level-2": {
  title: "Ultrasonic Testing (UT) – Level 2: Complete Theory, Inspection Techniques & Practical Mastery",

  content: (
    <div className="space-y-12">

      {/* =============================== */}
      {/* 1 — FUNDAMENTALS (IMAGE) */}
      {/* =============================== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <h3 className="text-xl font-semibold mb-3 border-b pb-1 text-blue-700">
            1. Fundamentals of Ultrasonic Testing
          </h3>

          <ul className="list-disc ml-6 space-y-2">
            <li><strong>What is UT?</strong> Uses high-frequency sound waves for flaw detection.</li>
            <li><strong>How it works:</strong> Sound → reflection → A-scan display.</li>
            <li><strong>Wave Behaviors:</strong> Reflection, Refraction, Attenuation.</li>
            <li><strong>Wave Types:</strong> Longitudinal, Shear, Rayleigh, Lamb.</li>
            <li><strong>Near & Far Field:</strong> Beam spread zones.</li>
          </ul>
        </div>

<div className="flex items-start justify-center mt-20 lg:mt-16">
  <div className="w-full" style={{ height: "220px" }}>
    <div className="w-full h-full bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden px-3 text-center">
      
      {/* Image placeholder - replace src with actual diagram */}
      <img
        src="/images/ut-fundamentals.png"
        alt="UT fundamentals diagram"
        className="w-full h-full object-contain"
        loading="lazy"
      />

    </div>
  </div>
</div>

      </div>


      {/* =============================== */}
      {/* 2 — EQUIPMENT (IMAGE) */}
      {/* =============================== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <h3 className="text-xl font-semibold mb-3 border-b pb-1 text-blue-700">
            2. UT Equipment, Probes & Accessories
          </h3>

          <ul className="list-disc ml-6 space-y-2">
            <li><strong>Machines:</strong> A-scan, digital flaw detectors.</li>
            <li><strong>Probes:</strong> Normal, angle, dual, immersion.</li>
            <li><strong>Parameters:</strong> Frequency, damping, size.</li>
            <li><strong>Wedges:</strong> 45°, 60°, 70°.</li>
            <li><strong>Couplants:</strong> Gel, oil, grease.</li>
          </ul>
        </div>

        <div className="flex items-start justify-center mt-20 lg:mt-16">
  <div className="w-full" style={{ height: "220px" }}>
    <div className="w-full h-full bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden px-3 text-center">
      
      {/* Image placeholder - replace src with actual diagram */}
      <img
        src="/images/ut-fundamentals.png"
        alt="UT fundamentals diagram"
        className="w-full h-full object-contain"
        loading="lazy"
      />

    </div>
  </div>
</div>

      </div>


      {/* =============================== */}
      {/* 3 — CALIBRATION (VIDEO) */}
      {/* =============================== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2">
          <h3 className="text-xl font-semibold mb-3 border-b pb-1 text-blue-700">
            3. Calibration Techniques
          </h3>

          <ul className="list-disc ml-6 space-y-2">
            <li>Range calibration</li>
            <li>Delay calibration</li>
            <li>Sensitivity calibration</li>
            <li>DAC curve plotting</li>
            <li>DGS / AVG</li>
            <li>IIW V1 / V2 blocks</li>
          </ul>
        </div>

<div className="flex items-start justify-center mt-20 lg:mt-16">
  <div className="w-full" style={{ height: "220px" }}>
    <div className="w-full h-full bg-black rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
      
      {/* Video placeholder - replace src with actual video file; poster optional */}
      <video
        src="/videos/calibration-demo.mp4"
        poster="/images/calibration-poster.jpg"
        controls
        playsInline
        className="w-full h-full object-contain bg-black"
      >
        Your browser does not support the video tag.
      </video>

    </div>
  </div>
</div>

      </div>


      {/* =============================== */}
      {/* 4 — WELD INSPECTION (VIDEO) */}
      {/* =============================== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2">
          <h3 className="text-xl font-semibold mb-3 border-b pb-1 text-blue-700">
            4. Weld Inspection Techniques
          </h3>

          <ul className="list-disc ml-6 space-y-2">
            <li>Half skip / Full skip / Multi skip scanning</li>
            <li>Probe manipulation patterns</li>
            <li>Beam paths (Leg 1 / Leg 2)</li>
            <li>Effect of weld geometry</li>
          </ul>
        </div>

<div className="flex items-start justify-center mt-20 lg:mt-16">
  <div className="w-full" style={{ height: "220px" }}>
    <div className="w-full h-full bg-black rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
      
      {/* Video placeholder - replace src with actual video file; poster optional */}
      <video
        src="/videos/calibration-demo.mp4"
        poster="/images/calibration-poster.jpg"
        controls
        playsInline
        className="w-full h-full object-contain bg-black"
      >
        Your browser does not support the video tag.
      </video>

    </div>
  </div>
</div>

      </div>


      {/* =============================== */}
      {/* 5 — DEFECT IDENTIFICATION (IMAGE) */}
      {/* =============================== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <h3 className="text-xl font-semibold mb-3 border-b pb-1 text-blue-700">
            5. Defect Identification & Sizing
          </h3>

          <ul className="list-disc ml-6 space-y-2">
            <li>LOF</li>
            <li>LOP</li>
            <li>Cracks</li>
            <li>Porosity</li>
            <li>Slag</li>
            <li>6 dB / 20 dB sizing</li>
          </ul>
        </div>
<div className="flex items-start justify-center mt-20 lg:mt-16">
  <div className="w-full" style={{ height: "220px" }}>
    <div className="w-full h-full bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden px-3 text-center">
      
      {/* Image placeholder - replace src with actual diagram */}
      <img
        src="/images/ut-fundamentals.png"
        alt="UT fundamentals diagram"
        className="w-full h-full object-contain"
        loading="lazy"
      />

    </div>
  </div>
</div>
      </div>


      {/* =============================== */}
      {/* 6 — REPORTING (IMAGE) */}
      {/* =============================== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <h3 className="text-xl font-semibold mb-3 border-b pb-1 text-blue-700">
            6. Reporting & Documentation
          </h3>

          <ul className="list-disc ml-6 space-y-2">
            <li>Probe angle & gain settings</li>
            <li>Location marking</li>
            <li>Depth vs distance plotting</li>
            <li>Final acceptance</li>
          </ul>
        </div>
<div className="flex items-start justify-center mt-20 lg:mt-16">
  <div className="w-full" style={{ height: "220px" }}>
    <div className="w-full h-full bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden px-3 text-center">
      
      {/* Image placeholder - replace src with actual diagram */}
      <img
        src="/images/ut-fundamentals.png"
        alt="UT fundamentals diagram"
        className="w-full h-full object-contain"
        loading="lazy"
      />

    </div>
  </div>
</div>
      </div>


      {/* =============================== */}
      {/* 7 — STANDARDS (IMAGE) */}
      {/* =============================== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <h3 className="text-xl font-semibold mb-3 border-b pb-1 text-blue-700">
            7. Codes, Standards & Acceptance Criteria
          </h3>

          <ul className="list-disc ml-6 space-y-2">
            <li>ASME V Article 4</li>
            <li>ASME VIII</li>
            <li>API 1104</li>
            <li>ISO 17640</li>
            <li>IS 13805</li>
          </ul>
        </div>

<div className="flex items-start justify-center mt-20 lg:mt-16">
  <div className="w-full" style={{ height: "220px" }}>
    <div className="w-full h-full bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden px-3 text-center">
      
      {/* Image placeholder - replace src with actual diagram */}
      <img
        src="/images/ut-fundamentals.png"
        alt="UT fundamentals diagram"
        className="w-full h-full object-contain"
        loading="lazy"
      />

    </div>
  </div>
</div>
      </div>


      {/* =============================== */}
      {/* 8 — PRACTICAL EXERCISES (VIDEO) */}
      {/* =============================== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2">
          <h3 className="text-xl font-semibold mb-3 border-b pb-1 text-blue-700">
            8. Practical Exercises
          </h3>

          <ul className="list-disc ml-6 space-y-2">
            <li>V1 Block Calibration</li>
            <li>DAC Plotting</li>
            <li>Weld Scanning</li>
            <li>Defect Plotting</li>
            <li>Report Preparation</li>
          </ul>
        </div>

      <div className="flex items-start justify-center mt-20 lg:mt-16">
  <div className="w-full" style={{ height: "220px" }}>
    <div className="w-full h-full bg-black rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
      
      {/* Video placeholder - replace src with actual video file; poster optional */}
      <video
        src="/videos/calibration-demo.mp4"
        poster="/images/calibration-poster.jpg"
        controls
        playsInline
        className="w-full h-full object-contain bg-black"
      >
        Your browser does not support the video tag.
      </video>

    </div>
  </div>
</div>

      </div>

    </div>
  ),
},






"paut-level-2": {
  title: "Phased Array Ultrasonic Testing (PAUT) – Level 2: Advanced Techniques",
  content: (
    <div className="space-y-16">

      {/* =============================== */}
      {/* 1 — PAUT THEORY & CONCEPTS */}
      {/* =============================== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* TEXT */}
        <div className="lg:col-span-2">
          <h3 className="text-xl font-semibold mb-3 border-b pb-1 text-blue-700">
            1. PAUT Theory & Core Concepts
          </h3>

          <ul className="list-disc ml-6 space-y-2">
            <li><strong>Electronic Scanning:</strong> Beam movement by activating element groups.</li>
            <li><strong>Beam Steering:</strong> Angled beams created by programmed time delays.</li>
            <li><strong>Beam Focusing:</strong> Time delays shaping convergence for higher sensitivity.</li>
            <li><strong>Focal Law Creation:</strong> Generating steering & focusing laws based on geometry.</li>
            <li><strong>Array Types:</strong> Linear, Matrix, Annular & Dual Matrix arrays.</li>
          </ul>
        </div>

        {/* IMAGE */}
        <div className="flex items-start justify-center mt-20 lg:mt-16">
          <div className="w-full" style={{ height: "220px" }}>
            <div className="w-full h-full bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden px-3 text-center">
              <img
                src="/images/paut-theory.png"
                alt="PAUT Theory Diagram"
                className="w-full h-full object-contain"
                loading="lazy"
              />
            </div>
          </div>
        </div>

      </div>



      {/* =============================== */}
      {/* 2 — PAUT EQUIPMENT */}
      {/* =============================== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* TEXT */}
        <div className="lg:col-span-2">
          <h3 className="text-xl font-semibold mb-3 border-b pb-1 text-blue-700">
            2. PAUT Equipment, Probes & Accessories
          </h3>

          <ul className="list-disc ml-6 space-y-2">
            <li><strong>PAUT Systems:</strong> OmniScan, Mentor UT, Epoch 1000, Sonatest Veo.</li>
            <li><strong>Probes:</strong> 16, 32, 64 element linear arrays.</li>
            <li><strong>Wedges:</strong> 36°, 45°, 55°, 60°, 70° – material & curvature specific.</li>
            <li><strong>Scanner Types:</strong> Manual scanners, encoded scanners, motorized systems.</li>
            <li><strong>Couplants:</strong> Gel, high-viscosity grease, immersion water tanks.</li>
          </ul>
        </div>

        {/* IMAGE */}
        <div className="flex items-start justify-center mt-20 lg:mt-16">
          <div className="w-full" style={{ height: "220px" }}>
            <div className="w-full h-full bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden px-3 text-center">
              <img
                src="/images/paut-equipment.png"
                alt="PAUT Equipment"
                className="w-full h-full object-contain"
                loading="lazy"
              />
            </div>
          </div>
        </div>

      </div>

      

      {/* =============================== */}
      {/* 3 — SCANNING METHODS (VIDEO) */}
      {/* =============================== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* TEXT */}
        <div className="lg:col-span-2">
          <h3 className="text-xl font-semibold mb-3 border-b pb-1 text-blue-700">
            3. PAUT Scanning Techniques
          </h3>

          <ul className="list-disc ml-6 space-y-2">
            <li><strong>S-Scan (Sectorial):</strong> Sweep angles for full weld coverage.</li>
            <li><strong>Linear Scan:</strong> Moves aperture along array length.</li>
            <li><strong>Dynamic Depth Focusing:</strong> Real-time focusing at multiple depths.</li>
            <li><strong>Compound Scans:</strong> Combination of linear + angular scanning.</li>
            <li><strong>Encoded Scanning:</strong> Capturing position vs amplitude vs time.</li>
          </ul>
        </div>

        {/* VIDEO */}
        <div className="flex items-start justify-center mt-20 lg:mt-16">
          <div className="w-full" style={{ height: "220px" }}>
            <div className="w-full h-full bg-black rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
              <video
                src="/videos/paut-scanning.mp4"
                poster="/images/paut-scan-poster.jpg"
                controls
                playsInline
                className="w-full h-full object-contain bg-black"
              />
            </div>
          </div>
        </div>

      </div>



      {/* =============================== */}
      {/* 4 — WELD INSPECTION */}
      {/* =============================== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* TEXT */}
        <div className="lg:col-span-2">
          <h3 className="text-xl font-semibold mb-3 border-b pb-1 text-blue-700">
            4. Weld Inspection Using PAUT
          </h3>

          <ul className="list-disc ml-6 space-y-2">
            <li><strong>Focal Law Setup:</strong> Based on weld thickness, bevel angle & root location.</li>
            <li><strong>Coverage Planning:</strong> Leg 1, Leg 2 & critical zone targeting.</li>
            <li><strong>Index Offset:</strong> Accurate wedge position alignment.</li>
            <li><strong>Skew Angle Application:</strong> For dissimilar and complex geometries.</li>
            <li><strong>Heat-Affected Zone (HAZ) Mapping</strong> using multiple scan angles.</li>
          </ul>
        </div>

        {/* IMAGE */}
        <div className="flex items-start justify-center mt-20 lg:mt-16">
          <div className="w-full" style={{ height: "220px" }}>
            <div className="w-full h-full bg-gray-100 rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center px-3">
              <img
                src="/images/paut-weld.png"
                alt="PAUT Weld Inspection Diagram"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>

      </div>



      {/* =============================== */}
      {/* 5 — DEFECT ANALYSIS */}
      {/* =============================== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* TEXT */}
        <div className="lg:col-span-2">
          <h3 className="text-xl font-semibold mb-3 border-b pb-1 text-blue-700">
            5. PAUT Defect Identification & Sizing
          </h3>

          <ul className="list-disc ml-6 space-y-2">
            <li><strong>Crack Detection:</strong> Toe cracks, root cracks, hot cracks.</li>
            <li><strong>Lack of Fusion:</strong> HAZ LOF, sidewall LOF, vertical LOF.</li>
            <li><strong>Porosity Clusters:</strong> Volumetric indications in S-Scan & C-Scan.</li>
            <li><strong>Slag & Inclusion Detection</strong> using multi-angle laws.</li>
            <li><strong>Advanced Sizing:</strong> DGS, Amplitude techniques & TCG.</li>
          </ul>
        </div>

        {/* IMAGE */}
        <div className="flex items-start justify-center mt-20 lg:mt-16">
          <div className="w-full" style={{ height: "220px" }}>
            <div className="w-full h-full bg-gray-100 rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center px-3">
              <img
                src="/images/paut-defects.png"
                alt="PAUT Defect Examples"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>

      </div>



      {/* =============================== */}
      {/* 6 — DATA INTERPRETATION (VIDEO) */}
      {/* =============================== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        <div className="lg:col-span-2">
          <h3 className="text-xl font-semibold mb-3 border-b pb-1 text-blue-700">
            6. PAUT Data Interpretation
          </h3>

          <ul className="list-disc ml-6 space-y-2">
            <li><strong>A-Scan:</strong> Raw signal analysis.</li>
            <li><strong>B-Scan:</strong> Through-depth profiling.</li>
            <li><strong>C-Scan:</strong> Top-view defect mapping.</li>
            <li><strong>S-Scan Interpretation:</strong> Key for weld analysis.</li>
            <li><strong>Encoded Data:</strong> Time vs amplitude vs position.</li>
          </ul>
        </div>

        {/* VIDEO */}
        <div className="flex items-start justify-center mt-20 lg:mt-16">
          <div className="w-full" style={{ height: "220px" }}>
            <div className="w-full h-full bg-black rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
              <video
                src="/videos/paut-data.mp4"
                poster="/images/paut-data-poster.jpg"
                controls
                playsInline
                className="w-full h-full object-contain bg-black"
              />
            </div>
          </div>
        </div>

      </div>



      {/* =============================== */}
      {/* 7 — STANDARDS */}
      {/* =============================== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        <div className="lg:col-span-2">
          <h3 className="text-xl font-semibold mb-3 border-b pb-1 text-blue-700">
            7. Codes, Standards & Industry Requirements
          </h3>

          <ul className="list-disc ml-6 space-y-2">
            <li>ASME Section V Article 4A (PAUT)</li>
            <li>API 1104 PAUT Acceptance Criteria</li>
            <li>ISO 13588 (PAUT weld testing)</li>
            <li>ISO 16811 (General Ultrasonic testing)</li>
            <li>TCG, DAC, PCS & Calibration Requirements</li>
          </ul>
        </div>

        <div className="flex items-start justify-center mt-20 lg:mt-16">
          <div className="w-full" style={{ height: "220px" }}>
            <div className="w-full h-full bg-gray-100 rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
              <img
                src="/images/paut-standards.png"
                alt="PAUT Standards"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>

      </div>



      {/* =============================== */}
      {/* 8 — PRACTICAL TRAINING */}
      {/* =============================== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        <div className="lg:col-span-2">
          <h3 className="text-xl font-semibold mb-3 border-b pb-1 text-blue-700">
            8. Practical PAUT Exercises
          </h3>

          <ul className="list-disc ml-6 space-y-2">
            <li>Wedge Calibration & Zeroing</li>
            <li>S-Scan Setup for V-groove & U-groove welds</li>
            <li>48–64 element focal law development</li>
            <li>Encoded Weld Scanning</li>
            <li>3D Visualization & Reporting</li>
          </ul>
        </div>

        <div className="flex items-start justify-center mt-20 lg:mt-16">
          <div className="w-full" style={{ height: "220px" }}>
            <div className="w-full h-full bg-black rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
              <video
                src="/videos/paut-practical.mp4"
                poster="/images/paut-practical-poster.jpg"
                controls
                playsInline
                className="w-full h-full object-contain bg-black"
              />
            </div>
          </div>
        </div>

      </div>

    </div>
  )
},

"simulations": {
    title: "Interactive NDT Simulations & Virtual Labs",
    content: (
        <>
            <p className="mb-4 text-lg text-red-700 font-medium">
                ⚠️ <strong>Note:</strong> These are placeholders for interactive simulation tools 
                that would typically be embedded or linked.
            </p>

            <ul className="list-disc ml-6 space-y-3">
                <li>
                    <strong>UT Calibration Simulator:</strong> A virtual tool to practice DAC/DGS 
                    calibration on different test blocks.
                </li>
                <li>
                    <strong>PAUT Focal Law Design Tool:</strong> An interactive interface to model 
                    beam steering and focusing for various probes and materials.
                </li>
                <li>
                    <strong>Weld Scan Interpretation Challenge:</strong> Upload mock PAUT data files 
                    and practice identifying and sizing defects in virtual welds.
                </li>
                <li>
                    <strong>Snell's Law Visualizer:</strong> An animation demonstrating refraction and 
                    mode conversion at material interfaces.
                </li>
            </ul>

            <blockquote className="mt-4 p-3 bg-yellow-100 border-l-4 border-yellow-500 italic text-sm">
                <strong>Goal:</strong> Practice fundamental NDT procedures in a risk-free, virtual 
                environment before moving to physical testing.
            </blockquote>

            {/* --- VIDEO BLOCK --- */}
            <div className="flex items-start justify-center mt-6 lg:mt-8">
                <div className="w-full" style={{ height: "200px" }}>
                    <div className="w-full h-full bg-black rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
                        <video
                            src="/videos/simulation-demo.mp4"
                            poster="/images/simulation-poster.jpg"
                            controls
                            playsInline
                            className="w-full h-full object-contain bg-black"
                        >
                            Your browser does not support the video tag.
                        </video>
                    </div>
                </div>
            </div>
        </>
    ),
},

"diagrams": {
    title: "Technical Schematics and Visual Aids",
    content: (
        <>
            <h3 className="text-xl font-semibold mb-3 border-b pb-1 text-blue-700">
                Essential UT & PAUT Visuals
            </h3>

            <ul className="list-disc ml-6 space-y-2">
                <li>
                    <strong>UT Straight Beam Diagrams:</strong> Schematic showing normal beam 
                    inspection for plate thickness measurement and lamination checks.
                </li>
                <li>
                    <strong>Angle Beam Diagrams:</strong> Clear illustrations of refracted angle, 
                    skip distance, and sound path.
                </li>
                <li>
                    <strong>Weld Inspection Sketches:</strong> Cross-sectional views of different 
                    weld types (V-groove, fillet) and optimal probe placement.
                </li>
                <li>
                    <strong>PAUT S-Scan Images:</strong> Example S-scan showing common flaws like 
                    root erosion or lack of fusion.
                </li>
                <li>
                    <strong>TFM Visual Examples:</strong> High-resolution images comparing a standard 
                    PAUT C-scan to a TFM result for the same flaw.
                </li>
            </ul>

            <p className="mt-4 text-sm text-gray-600">
                *All schematics are high-resolution and annotated with NDT terminology.*
            </p>

            {/* --- MEDIA PLACEHOLDER BLOCK --- */}
<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6 lg:mt-8">

  {/* Image 1 */}
  <div className="w-full" style={{ height: "200px" }}>
    <div className="w-full h-full bg-black rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
      <img
        src="/images/diagram-placeholder-1.png"
        alt="Diagram Placeholder 1"
        className="w-full h-full object-contain bg-black"
      />
    </div>
  </div>

  {/* Image 2 */}
  <div className="w-full" style={{ height: "200px" }}>
    <div className="w-full h-full bg-black rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
      <img
        src="/images/diagram-placeholder-2.png"
        alt="Diagram Placeholder 2"
        className="w-full h-full object-contain bg-black"
      />
    </div>
  </div>

  {/* Image 3 */}
  <div className="w-full" style={{ height: "200px" }}>
    <div className="w-full h-full bg-black rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
      <img
        src="/images/diagram-placeholder-3.png"
        alt="Diagram Placeholder 3"
        className="w-full h-full object-contain bg-black"
      />
    </div>
  </div>

  {/* Image 4 */}
  <div className="w-full" style={{ height: "200px" }}>
    <div className="w-full h-full bg-black rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
      <img
        src="/images/diagram-placeholder-4.png"
        alt="Diagram Placeholder 4"
        className="w-full h-full object-contain bg-black"
      />
    </div>
  </div>

</div>

        </>
    ),
},


"tools": {
    title: "NDT Calculators & Practical Tools",
    content: (
        <>
            {/* Title */}
            <h3 className="text-xl font-semibold mb-3 border-b pb-1 text-blue-700">
                Online NDT Calculation Tools
            </h3>

            {/* Description List */}
            <ul className="list-disc ml-6 space-y-3">
                <li>
                    <b>Decibel (dB) Drop Calculator:</b>  
                    Compute amplitude reduction, signal loss, and compare
                    reference vs. indication levels using  
                    <span className="font-mono">ΔdB = 20 log (A₂ / A₁)</span>.
                </li>
                <li>
                    <b>Skip Distance Calculator:</b>  
                    Calculate <b>½ skip</b>, <b>1 skip</b>, and <b>1.5 skip</b> distances 
                    based on probe refracted angle and material thickness.
                </li>
                <li>
                    <b>Snell's Law Calculator:</b>  
                    Determine longitudinal & shear refracted angles using  
                    <span className="font-mono">sinθ₂ = (V₂ / V₁) sinθ₁</span>.
                </li>
                <li>
                    <b>PAUT Coverage Calculator:</b>  
                    Visualize coverage zone for different focal laws, sweep 
                    angles, index offset, and wedge parameters.
                </li>
                <li>
                    <b>Beam Path Visualizer:</b>  
                    Generate ray-trace diagrams for UT & PAUT based on wedge, thickness, and velocity.
                </li>
            </ul>

            <p className="mt-4 text-sm text-gray-600">
                *These tools accept user inputs (thickness, velocity, angles, frequency, wedge type) and instantly compute UT/PAUT results.*
            </p>

            {/* Multi-Image Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">

                {/* Tool Image 1 */}
                <div className="w-full" style={{ height: "200px" }}>
                    <div className="w-full h-full bg-black rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
                        <img
                            src="/images/tool-db-calculator.png"
                            alt="dB Drop Calculator UI"
                            className="w-full h-full object-contain bg-black"
                        />
                    </div>
                </div>

                {/* Tool Image 2 */}
                <div className="w-full" style={{ height: "200px" }}>
                    <div className="w-full h-full bg-black rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
                        <img
                            src="/images/tool-skip-distance.png"
                            alt="Skip Distance Calculator"
                            className="w-full h-full object-contain bg-black"
                        />
                    </div>
                </div>

                {/* Tool Image 3 */}
                <div className="w-full" style={{ height: "200px" }}>
                    <div className="w-full h-full bg-black rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
                        <img
                            src="/images/tool-snells-law.png"
                            alt="Snell's Law Calculator"
                            className="w-full h-full object-contain bg-black"
                        />
                    </div>
                </div>

                {/* Tool Image 4 */}
                <div className="w-full" style={{ height: "200px" }}>
                    <div className="w-full h-full bg-black rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
                        <img
                            src="/images/tool-paut-coverage.png"
                            alt="PAUT Coverage Calculator"
                            className="w-full h-full object-contain bg-black"
                        />
                    </div>
                </div>
            </div>

            {/* Optional Tool Demo Video */}
            <div className="flex items-start justify-center mt-10">
                <div className="w-full" style={{ height: "240px" }}>
                    <div className="w-full h-full bg-black rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
                        <video
                            src="/videos/ndt-tool-demo.mp4"
                            poster="/images/ndt-tools-poster.jpg"
                            controls
                            playsInline
                            className="w-full h-full object-contain bg-black"
                        >
                            Your browser does not support the video tag.
                        </video>
                    </div>
                </div>
            </div>
        </>
    ),
},



"articles": {
    title: "In-Depth Articles & Technical Blogs",
    content: (
        <>

            {/* Header */}
            <h3 className="text-xl font-semibold mb-3 border-b pb-1 text-blue-700">
                Featured NDT Technical Topics
            </h3>

            {/* Article List */}
            <ul className="list-disc ml-6 space-y-3 text-gray-800">

                <li>
                    <b>Understanding TFM (Total Focusing Method):</b>  
                    A deep dive into the physics behind Full Matrix Capture (FMC) and TFM, image reconstruction algorithms, 
                    resolution improvements, and practical scenarios where TFM outperforms conventional PAUT.
                </li>

                <li>
                    <b>PAUT vs. Conventional UT – A Complete Comparison:</b>  
                    Covers probe technology, coverage, sizing accuracy, probability of detection (POD), 
                    inspection speed, weld scanning capabilities, and limitations of each method.
                </li>

                <li>
                    <b>Best Practices for Pipe & Girth Weld Inspection:</b>  
                    Discusses the combined use of TOFD, PAUT, and manual UT for pipelines, 
                    defect characterization techniques, HAZ scanning, and code compliance (API 1104, ASME).
                </li>

                <li>
                    <b>Why PAUT is Replacing Radiography (RT):</b>  
                    A technical and safety-based evaluation of PAUT vs. RT, including radiation-free workflow, 
                    instant results, volumetric imaging, cost savings, and industry adoption trends.
                </li>

                <li>
                    <b>Digital NDT Trends for 2025:</b>  
                    The shift toward AI-assisted reporting, offline FMC/TFM processing, automated scanners, 
                    and cloud-based workflow management.
                </li>
            </ul>

            {/* Highlight Notice */}
            <blockquote className="mt-5 p-4 bg-indigo-100 border-l-4 border-indigo-500 text-sm rounded-md">
                <b>Stay Updated:</b> New expert-written articles are added weekly, covering advanced NDT technologies, 
                inspection codes, industry case studies, and modern techniques used in Oil & Gas, Aerospace, Power, and Manufacturing.
            </blockquote>

            {/* Images Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">

                {/* Article Image 1 */}
                <div className="w-full" style={{ height: "180px" }}>
                    <div className="w-full h-full bg-black border border-gray-200 rounded-lg overflow-hidden flex justify-center items-center">
                        <img
                            src="/images/article-tfm.png"
                            alt="TFM Technical Article"
                            className="w-full h-full object-contain"
                        />
                    </div>
                </div>

                {/* Article Image 2 */}
                <div className="w-full" style={{ height: "180px" }}>
                    <div className="w-full h-full bg-black border border-gray-200 rounded-lg overflow-hidden flex justify-center items-center">
                        <img
                            src="/images/article-paut-vs-ut.png"
                            alt="PAUT vs UT"
                            className="w-full h-full object-contain"
                        />
                    </div>
                </div>

                {/* Article Image 3 */}
                <div className="w-full" style={{ height: "180px" }}>
                    <div className="w-full h-full bg-black border border-gray-200 rounded-lg overflow-hidden flex justify-center items-center">
                        <img
                            src="/images/article-girth-weld.png"
                            alt="Pipe & Girth Weld Inspection"
                            className="w-full h-full object-contain"
                        />
                    </div>
                </div>
            </div>

            {/* Optional Video Preview */}
            <div className="flex items-center justify-center mt-10">
                <div className="w-full" style={{ height: "220px" }}>
                    <div className="w-full h-full bg-black border border-gray-300 rounded-lg overflow-hidden flex items-center justify-center">
                        <video
                            src="/videos/ndt-article-intro.mp4"
                            poster="/images/ndt-article-poster.jpg"
                            className="w-full h-full object-contain"
                            controls
                            playsInline
                        >
                            Your browser does not support the video tag.
                        </video>
                    </div>
                </div>
            </div>

        </>
    ),
},



"downloads": {
    title: "Premium Downloadable Resources",
    content: (
        <>

            {/* Premium Notice */}
            <p className="mb-4 text-lg text-green-700 font-medium">
                🔑 <b>Premium Content – Login & Payment Required</b>
            </p>

            {/* Resource List Description */}
            <p className="text-gray-700 mb-4 text-sm">
                Access high-quality, instructor-level training materials designed for UT & PAUT Level II preparation. 
                These downloads are locked until the user completes payment through your integrated checkout system.
            </p>

            {/* Resource Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">

                {/* ITEM 1 */}
                <div className="border rounded-xl shadow-sm p-4 bg-white flex flex-col">
                    <h4 className="font-semibold text-blue-700 mb-2">UT Level II Complete Theory E-Book (PDF)</h4>
                    <p className="text-sm text-gray-600 mb-3">
                        250+ pages covering fundamentals, calibration, weld inspection, sizing, and practice questions.
                    </p>

                    {/* Preview Image */}
                    <div className="w-full h-40 bg-gray-100 border rounded-lg overflow-hidden flex items-center justify-center mb-3">
                        <img 
                            src="/pdf-previews/ut-level2-book.png"
                            alt="UT Level II PDF Preview"
                            className="w-full h-full object-contain"
                        />
                    </div>

                    {/* Locked Button */}
                    <button 
                        className="bg-gray-300 text-gray-600 py-2 rounded-lg cursor-not-allowed font-medium"
                    >
                        🔒 Login & Pay to Download
                    </button>
                </div>

                {/* ITEM 2 */}
                <div className="border rounded-xl shadow-sm p-4 bg-white flex flex-col">
                    <h4 className="font-semibold text-blue-700 mb-2">PAUT Level II Study Guide (PDF)</h4>
                    <p className="text-sm text-gray-600 mb-3">
                        Covers focal laws, S-scan interpretation, weld scanning, TFM basics, and advanced exercises.
                    </p>

                    <div className="w-full h-40 bg-gray-100 border rounded-lg overflow-hidden flex items-center justify-center mb-3">
                        <img 
                            src="/pdf-previews/paut-guide.png"
                            alt="PAUT Study Guide Preview"
                            className="w-full h-full object-contain"
                        />
                    </div>

                    <button 
                        className="bg-gray-300 text-gray-600 py-2 rounded-lg cursor-not-allowed font-medium"
                    >
                        🔒 Login & Pay to Download
                    </button>
                </div>

                {/* ITEM 3 */}
                <div className="border rounded-xl shadow-sm p-4 bg-white flex flex-col">
                    <h4 className="font-semibold text-blue-700 mb-2">UT / PAUT Training PPT Pack</h4>
                    <p className="text-sm text-gray-600 mb-3">
                        Professionally designed PPT slides used by certified trainers for classrooms & workshops.
                    </p>

                    <div className="w-full h-40 bg-gray-100 border rounded-lg overflow-hidden flex items-center justify-center mb-3">
                        <img 
                            src="/pdf-previews/ppt-pack.png"
                            alt="Training PPT Preview"
                            className="w-full h-full object-contain"
                        />
                    </div>

                    <button 
                        className="bg-gray-300 text-gray-600 py-2 rounded-lg cursor-not-allowed font-medium"
                    >
                        🔒 Login & Pay to Download
                    </button>
                </div>

                {/* ITEM 4 */}
                <div className="border rounded-xl shadow-sm p-4 bg-white flex flex-col">
                    <h4 className="font-semibold text-blue-700 mb-2">Combined UT + PAUT Master Book</h4>
                    <p className="text-sm text-gray-600 mb-3">
                        Hardcover + Digital version. Ultimate reference including diagrams, formulas, and real case studies.
                    </p>

                    <div className="w-full h-40 bg-gray-100 border rounded-lg overflow-hidden flex items-center justify-center mb-3">
                        <img 
                            src="/pdf-previews/master-book.png"
                            alt="Master Book Preview"
                            className="w-full h-full object-contain"
                        />
                    </div>

                    <button 
                        className="bg-gray-300 text-gray-600 py-2 rounded-lg cursor-not-allowed font-medium"
                    >
                        🔒 Purchase Required
                    </button>
                </div>

                {/* ITEM 5 */}
                <div className="border rounded-xl shadow-sm p-4 bg-white flex flex-col">
                    <h4 className="font-semibold text-blue-700 mb-2">High-Resolution NDT Image Packs</h4>
                    <p className="text-sm text-gray-600 mb-3">
                        500+ defect photos, PAUT S-scans, TFM images, weld sketches, calibration blocks & diagrams.
                    </p>

                    <div className="w-full h-40 bg-gray-100 border rounded-lg overflow-hidden flex items-center justify-center mb-3">
                        <img 
                            src="/pdf-previews/image-pack.png"
                            alt="NDT Image Pack Preview"
                            className="w-full h-full object-contain"
                        />
                    </div>

                    <button 
                        className="bg-gray-300 text-gray-600 py-2 rounded-lg cursor-not-allowed font-medium"
                    >
                        🔒 Buy to Unlock
                    </button>
                </div>

            </div>

            {/* Bottom Info */}
            <p className="mt-6 text-xs text-gray-500">
                *All resources are copyrighted and for personal learning use only. Redistribution is strictly prohibited.*
            </p>

        </>
    ),
},

};

// ... (imports and constants remain the same) ...

export default function Resources() {
    const [selected, setSelected] = useState("ut-level-2");
    const [sidebarOpen, setSidebarOpen] = useState(false);

    // Get content for the selected item
    const selectedContent = contentData[selected];

    // Find the label for the currently selected item
    const currentLabel = sidebarItems.find(item => item.id === selected)?.label || 'Resources';

    // The main layout starts here. We'll simulate a top navbar
    return (
        <div className="flex flex-col h-screen bg-gray-50">

            {/* SIMULATED DESKTOP NAVBAR (for alignment reference) */}
            <div className={`hidden lg:block fixed top-0 left-0 right-0 bg-white shadow-md z-30 ${DESKTOP_NAVBAR_HEIGHT_CLASS} border-b`}>
                <div className="flex items-center h-full px-6">
                    <span className="text-2xl font-bold text-gray-900">Application Navbar</span>
                    {/* Other navbar items would go here */}
                </div>
            </div>

            {/* MOBILE HEADER - Fixed at the top, visible only on mobile */}
            <div className={`lg:hidden fixed top-0 left-0 right-0 bg-white shadow-lg z-40 flex items-center justify-between px-4 ${MOBILE_HEADER_HEIGHT_CLASS}`}>
                <h1 className="text-xl font-bold text-gray-800">NDT Resources Hub</h1>
                <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="p-1 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
                    aria-label={sidebarOpen ? "Close Menu" : "Open Menu"}
                >
                    {sidebarOpen ? <X size={26} /> : <Menu size={26} />}
                </button>
            </div>

            {/* MAIN CONTENT + SIDEBAR CONTAINER - Starts below the desktop navbar */}
            <div className="flex flex-1 flex-col lg:flex-row relative">

                {/* SIDEBAR - Fixed on large screens, slides in on mobile */}
                {/* CRITICAL FIX: Use Tailwind classes for positioning: top-14 (3.5rem) on mobile, lg:top-16 (4rem) on desktop */}
                <div
                    className={`
    fixed lg:static left-0 z-30 
    w-64 bg-white shadow-xl border-r border-gray-200
    transform transition-transform duration-300 ease-in-out
    ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
    lg:translate-x-0
    overflow-y-auto
    
    // Mobile: Starts below 64px (navbar height)
    top-22 h-[calc(100vh-4rem)] 
    
    // Desktop: Starts below desktop navbar
    lg:top-16 lg:h-[calc(100vh-4rem)] lg:min-h-screen
  `}
                >

                    {/* Sidebar Title - Hidden on mobile because it's in the top fixed header */}
                    <h2 className="hidden lg:block text-2xl font-extrabold text-blue-600 p-4 border-b border-gray-100">NDT Library</h2>

                    {/* Sidebar Items */}
                    <ul className="p-4 space-y-1">
                        {sidebarItems.map((item) => (
                            <li
                                key={item.id}
                                onClick={() => {
                                    setSelected(item.id);
                                    setSidebarOpen(false);
                                }}
                                className={`
                  flex items-center space-x-3 p-3 rounded-lg cursor-pointer text-base
                  font-medium transition-colors duration-200
                  ${selected === item.id
                                        ? "bg-blue-600 text-white shadow-md"
                                        : "text-gray-700 hover:bg-blue-50 hover:text-blue-700"}
                `}
                            >
                                {item.icon}
                                <span>{item.label}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* MAIN CONTENT AREA - Padding/Margin to clear fixed headers */}
                {/* CRITICAL FIX: Ensure main content margin matches the navbar/header size */}
                <div className="flex-1 p-4 lg:p-6 pt-4 lg:pt-6 overflow-y-auto max-h-[calc(100vh-120px)]">

                    {/* Mobile Section Toggle Button */}
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className="lg:hidden flex items-center justify-start 
               text-base font-semibold text-gray-800 
               bg-gray-100 hover:bg-gray-200 
               w-full p-4 mb-4 rounded-xl 
               transition-all shadow-sm"
                        aria-label={`Open menu to change section from ${currentLabel}`}
                    >
                        <Menu size={22} className="mr-3 text-blue-600" />
                        <span className="truncate">Current Section: {currentLabel}</span>
                    </button>

                    {/* Main Title */}
                    <header className="mb-6 pb-2 border-b-2 border-blue-500">
                        <h1 className="text-3xl font-extrabold text-gray-900">{selectedContent.title}</h1>
                    </header>

                    {/* Content Box */}
                    <div className="bg-white p-6 shadow-2xl rounded-xl text-gray-800 leading-relaxed">
                        {typeof selectedContent.content === 'string'
                            ? <div className="whitespace-pre-line">{selectedContent.content}</div>
                            : selectedContent.content
                        }
                    </div>

                </div>


                {/* Overlay to close sidebar on mobile when open */}
                {sidebarOpen && (
                    <div
                        className="fixed inset-0 bg-black opacity-50 z-20 lg:hidden"
                        onClick={() => setSidebarOpen(false)}
                    ></div>
                )}

            </div>
        </div>
    );
}